import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"

const JQUERY_NAMESPACE = ".otherOrders"

// Paired with the shared `datatable` controller on the same wrapping element: that one owns the
// DataTables lifecycle, this one owns the date-range picker and the two select2 filters.
//
// Both filters really do narrow the table: the view's select_tags render with class
// `datatable-moveable-filter`, which the listener below matches, and
// OrderReceipt.datatable_relation reads params[:filter][:transaction_type] and
// params[:filter][:ordered_by].
export default class extends Controller {
  static get targets() {
    return ["table", "dateRangeInput", "transactionTypeFilter", "orderedByFilter"];
  }

  static get values() {
    return { begin: String, end: String };
  }

  connect() {
    $(this.dateRangeInputTarget).daterangepicker({
      buttonClasses: "btn btn-main btn-md",
      cancelButtonClasses: "btn-ghost",
      opens: "left",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      ranges: {
        "Today": [moment(), moment()],
        "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
        "All Time": [moment(this.beginValue, "MM-DD-YYYY"), moment(this.endValue, "MM-DD-YYYY")],
      },
    }, (start, end) => this.applyRange(start, end));

    $(this.transactionTypeFilterTarget).select2({ width: "100%", minimumResultsForSearch: Infinity });
    $(this.orderedByFilterTarget).select2({ width: "100%", placeholder: "User", minimumResultsForSearch: Infinity });

    // select2 announces a pick with jQuery's `.trigger("change")`, not a native dispatch, so this
    // has to stay on jQuery's event bus rather than becoming a data-action.
    $(this.element).on(`change${JQUERY_NAMESPACE}`, ".datatable-moveable-filter", () => this.reload());
  }

  disconnect() {
    const picker = $(this.dateRangeInputTarget).data("daterangepicker");
    if (picker) { picker.remove(); }
    $(this.dateRangeInputTarget).off(JQUERY_NAMESPACE);

    $(this.element).off(JQUERY_NAMESPACE);

    if ($(this.transactionTypeFilterTarget).data("select2")) {
      $(this.transactionTypeFilterTarget).select2("destroy");
    }
    if ($(this.orderedByFilterTarget).data("select2")) {
      $(this.orderedByFilterTarget).select2("destroy");
    }
  }

  applyRange(start, end) {
    const newRange = `${start.format("MMDDYYYY")}-${end.format("MMDDYYYY")}`;
    this.reload({ range: newRange });
  }

  reload(extraParams = {}) {
    ProgressBar.start();

    const filterSelects = this.element.querySelectorAll(".datatable-moveable-filter, .datatable-filter");
    if (filterSelects.length > 0) {
      const filter = {};
      filterSelects.forEach((el) => {
        const key = el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "");
        // select2's own data API, not a plain DOM read -- has to stay jQuery-wrapped.
        filter[key] = ($(el).select2("data") || []).map((option) => option.id);
      });
      extraParams["filter"] = filter;
    }

    // No mounted view renders a `.datatable-moveable-text-filter`/`.datatable-text-filter`
    // element for this controller today, so this loop never runs in practice -- kept for parity
    // with the sibling datatable controllers that share this reload() shape (patterns.md 29).
    this.element.querySelectorAll(".datatable-moveable-text-filter, .datatable-text-filter").forEach((el) => {
      extraParams["filter"][el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")] = el.value;
    });

    const params = Object.assign(this.currentParams(), extraParams);

    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`;
    this.tableTarget.setAttribute("data-source", replacement);
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"));
  }

  // Converts a bracket-notation query string into the nested object `$.param()` re-serializes
  // back into the same form. A URLSearchParams rewrite would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }
}
